import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { appColors, appEffects } from '../../config/appTheme';
import type { BPlanGeometry } from '../../types/bebauungsplan';

interface BPlanGeometryEditorProps {
  /** GeoJSON Polygon/MultiPolygon (WGS84). Null = nothing drawn yet. */
  value: BPlanGeometry | null;
  onChange: (geometry: BPlanGeometry | null) => void;
  /** Initial map center [lat, lon]; defaults to deutsche Mitte if no value. */
  initialCenter?: [number, number];
  height?: string;
  readOnly?: boolean;
}

type Mode = 'view' | 'drawing' | 'editing';

// ---- Selbst-Überschneidungs-Schutz ------------------------------------------
// Standard-2D-Segmentschnitt (lon/lat ebenenmäßig — auf Stadtteil-Maßstab ok).

function segmentsCross(a: L.LatLng, b: L.LatLng, c: L.LatLng, d: L.LatLng): boolean {
  // Gemeinsame Endpunkte → benachbarte Kanten → kein "echter" Schnitt.
  if ((a.lat === c.lat && a.lng === c.lng) || (a.lat === d.lat && a.lng === d.lng) ||
      (b.lat === c.lat && b.lng === c.lng) || (b.lat === d.lat && b.lng === d.lng)) {
    return false;
  }
  const ccw = (p: L.LatLng, q: L.LatLng, r: L.LatLng) =>
    (r.lat - p.lat) * (q.lng - p.lng) - (q.lat - p.lat) * (r.lng - p.lng);
  const d1 = ccw(c, d, a);
  const d2 = ccw(c, d, b);
  const d3 = ccw(a, b, c);
  const d4 = ccw(a, b, d);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
         ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

/** Würde ein neues Segment vom letzten Draft-Punkt zu `newPoint` eine frühere Kante kreuzen? */
function draftCrossesItself(draft: L.LatLng[], newPoint: L.LatLng): boolean {
  if (draft.length < 2) return false;
  const last = draft[draft.length - 1];
  for (let i = 0; i < draft.length - 2; i++) {
    if (segmentsCross(draft[i], draft[i + 1], last, newPoint)) return true;
  }
  return false;
}

/** Würde das Schließen des Drafts (letzter Punkt → erster Punkt) eine frühere Kante kreuzen? */
function draftClosingCrosses(draft: L.LatLng[]): boolean {
  if (draft.length < 4) return false;
  const last = draft[draft.length - 1];
  const first = draft[0];
  // Schließsegment grenzt an Segment 0 (first→draft[1]) und Segment draft.length-2 (draft[n-2]→last).
  for (let i = 1; i < draft.length - 2; i++) {
    if (segmentsCross(draft[i], draft[i + 1], last, first)) return true;
  }
  return false;
}

/** Ist ein geschlossener Ring überschneidungsfrei (für Drag-Validierung)? */
function ringIsSimple(ring: L.LatLng[]): boolean {
  const n = ring.length;
  if (n < 4) return true;
  for (let i = 0; i < n; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % n];
    for (let j = i + 2; j < n; j++) {
      // Nachbarn überspringen (auch über das Ring-Ende hinweg)
      if (i === 0 && j === n - 1) continue;
      const c = ring[j];
      const d = ring[(j + 1) % n];
      if (segmentsCross(a, b, c, d)) return false;
    }
  }
  return true;
}

// ---- GeoJSON <-> LatLng ringe ------------------------------------------------

function geometryToRings(geometry: BPlanGeometry | null): L.LatLng[][] {
  if (!geometry) return [];
  if (geometry.type === 'Polygon') {
    const ring = geometry.coordinates[0];
    if (!ring || ring.length < 3) return [];
    return [stripClosingPoint(ring.map(([lon, lat]) => L.latLng(lat, lon)))];
  }
  return geometry.coordinates
    .map(poly => poly[0])
    .filter(r => r && r.length >= 3)
    .map(ring => stripClosingPoint(ring.map(([lon, lat]) => L.latLng(lat, lon))));
}

function stripClosingPoint(ring: L.LatLng[]): L.LatLng[] {
  if (ring.length < 2) return ring;
  const a = ring[0];
  const b = ring[ring.length - 1];
  if (a.lat === b.lat && a.lng === b.lng) return ring.slice(0, -1);
  return ring;
}

function ringsToGeometry(rings: L.LatLng[][]): BPlanGeometry | null {
  const valid = rings.filter(r => r.length >= 3);
  if (valid.length === 0) return null;
  const toClosedRing = (r: L.LatLng[]): number[][] => {
    const coords = r.map(p => [p.lng, p.lat] as [number, number]);
    coords.push([coords[0][0], coords[0][1]]);
    return coords;
  };
  if (valid.length === 1) {
    return { type: 'Polygon', coordinates: [toClosedRing(valid[0])] };
  }
  return { type: 'MultiPolygon', coordinates: valid.map(r => [toClosedRing(r)]) };
}

// ---- Drawing/editing layer (rendered inside MapContainer) -------------------

interface DrawLayerProps {
  rings: L.LatLng[][];
  setRings: (rings: L.LatLng[][]) => void;
  mode: Mode;
  draftPoints: L.LatLng[];
  setDraftPoints: (pts: L.LatLng[]) => void;
  fitOnLoad: boolean;
  setFitOnLoad: (b: boolean) => void;
}

function DrawLayer({
  rings,
  setRings,
  mode,
  draftPoints,
  setDraftPoints,
  fitOnLoad,
  setFitOnLoad,
}: DrawLayerProps) {
  const map = useMap();
  const finishedLayerRef = useRef<L.LayerGroup | null>(null);
  const draftLayerRef = useRef<L.LayerGroup | null>(null);

  // Create groups (and re-create after Map remount because useMap returns new map)
  useEffect(() => {
    const finished = L.layerGroup().addTo(map);
    const draft = L.layerGroup().addTo(map);
    finishedLayerRef.current = finished;
    draftLayerRef.current = draft;
    return () => {
      finished.remove();
      draft.remove();
      finishedLayerRef.current = null;
      draftLayerRef.current = null;
    };
  }, [map]);

  // Auto-fit once when initial value present
  useEffect(() => {
    if (!fitOnLoad) return;
    if (rings.length === 0) return;
    const all = rings.flat();
    if (all.length === 0) return;
    const bounds = L.latLngBounds(all);
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    }
    setFitOnLoad(false);
  }, [fitOnLoad, rings, map, setFitOnLoad]);

  // Re-render finished polygons + vertex handles
  useEffect(() => {
    const group = finishedLayerRef.current;
    if (!group) return;
    group.clearLayers();

    rings.forEach((ring, ringIdx) => {
      const polygon = L.polygon(ring, {
        color: appColors.primary[600],
        weight: 2,
        fillColor: appColors.primary[500],
        fillOpacity: 0.2,
        interactive: false,
      });
      polygon.addTo(group);

      if (mode === 'editing') {
        ring.forEach((pt, vertexIdx) => {
          const handle = L.marker(pt, {
            draggable: true,
            icon: L.divIcon({
              className: '',
              iconSize: [12, 12],
              iconAnchor: [6, 6],
              html:
                `<div style="width:12px;height:12px;background:${appColors.white};border:2px solid ${appColors.primary[600]};border-radius:2px;box-shadow:${appEffects.vertexShadow};"></div>`,
            }),
          });
          handle.on('drag', (e) => {
            const newLatLng = (e.target as L.Marker).getLatLng();
            const newRings = rings.map((r, i) =>
              i === ringIdx ? r.map((p, j) => (j === vertexIdx ? newLatLng : p)) : r,
            );
            // Live-update the polygon shape (without re-rendering markers)
            polygon.setLatLngs(newRings[ringIdx]);
          });
          handle.on('dragend', (e) => {
            const newLatLng = (e.target as L.Marker).getLatLng();
            const newRing = ring.map((p, j) => (j === vertexIdx ? newLatLng : p));
            if (!ringIsSimple(newRing)) {
              // Snap zurück: Polygon und Handle auf alten Stand setzen
              polygon.setLatLngs(ring);
              (e.target as L.Marker).setLatLng(ring[vertexIdx]);
              return;
            }
            const newRings = rings.map((r, i) => (i === ringIdx ? newRing : r));
            setRings(newRings);
          });
          handle.on('contextmenu', (e) => {
            // Right-click to remove a vertex (only if ring still has ≥4 vertices)
            const ev = (e as L.LeafletMouseEvent).originalEvent;
            if (ev) ev.preventDefault();
            if (ring.length <= 3) return;
            const newRing = ring.filter((_, j) => j !== vertexIdx);
            const newRings = rings.map((r, i) => (i === ringIdx ? newRing : r));
            setRings(newRings);
          });
          handle.addTo(group);
        });
      }
    });
  }, [rings, mode, setRings]);

  // Re-render draft (während des Zeichnens)
  useEffect(() => {
    const group = draftLayerRef.current;
    if (!group) return;
    group.clearLayers();
    if (mode !== 'drawing') return;

    if (draftPoints.length >= 2) {
      L.polyline(draftPoints, {
        color: appColors.primary[600],
        weight: 2,
        dashArray: '6,4',
        interactive: false,
      }).addTo(group);
    }
    if (draftPoints.length >= 3) {
      L.polygon(draftPoints, {
        color: appColors.primary[600],
        weight: 1,
        fillColor: appColors.primary[500],
        fillOpacity: 0.15,
        interactive: false,
        dashArray: '4,4',
      }).addTo(group);
    }
    draftPoints.forEach((pt, idx) => {
      L.marker(pt, {
        interactive: false,
        icon: L.divIcon({
          className: '',
          iconSize: [10, 10],
          iconAnchor: [5, 5],
          html: `<div style="width:10px;height:10px;background:${appColors.primary[600]};border:2px solid ${appColors.white};border-radius:50%;box-shadow:${appEffects.draftVertexShadow};" title="Punkt ${idx + 1}"></div>`,
        }),
      }).addTo(group);
    });
  }, [draftPoints, mode]);

  // Click handler when drawing
  useEffect(() => {
    if (mode !== 'drawing') return;
    const onClick = (e: L.LeafletMouseEvent) => {
      if (draftCrossesItself(draftPoints, e.latlng)) {
        // Ignoriere Klicks, die das eigene Polygon kreuzen würden.
        // (Cursor-Form könnte hier zusätzlich auf "not-allowed" wechseln — UX-Polish.)
        return;
      }
      setDraftPoints([...draftPoints, e.latlng]);
    };
    map.on('click', onClick);
    return () => {
      map.off('click', onClick);
    };
  }, [mode, draftPoints, map, setDraftPoints]);

  // Set cursor while drawing
  useEffect(() => {
    const container = map.getContainer();
    if (mode === 'drawing') {
      container.style.cursor = 'crosshair';
    } else {
      container.style.cursor = '';
    }
    return () => {
      container.style.cursor = '';
    };
  }, [mode, map]);

  return null;
}

// ---- Editor outer component --------------------------------------------------

export default function BPlanGeometryEditor({
  value,
  onChange,
  initialCenter,
  height = '500px',
  readOnly = false,
}: BPlanGeometryEditorProps) {
  // Local working copy as latlng rings; sync to parent via onChange.
  const [rings, setRingsLocal] = useState<L.LatLng[][]>(() => geometryToRings(value));
  const [mode, setMode] = useState<Mode>('view');
  const [draftPoints, setDraftPoints] = useState<L.LatLng[]>([]);
  const [fitOnLoad, setFitOnLoad] = useState(true);
  const lastEmittedRef = useRef<string>('');

  // Re-sync from parent only when the incoming value changes shape — avoids
  // resetting local state on every render (which would kill in-progress edits).
  useEffect(() => {
    const incoming = geometryToRings(value);
    const incomingKey = JSON.stringify(value);
    if (incomingKey !== lastEmittedRef.current) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Reconcile the controlled geometry without overwriting local edits.
      setRingsLocal(incoming);
      lastEmittedRef.current = incomingKey;
    }
  }, [value]);

  const setRings = (newRings: L.LatLng[][]) => {
    setRingsLocal(newRings);
    const geom = ringsToGeometry(newRings);
    lastEmittedRef.current = JSON.stringify(geom);
    onChange(geom);
  };

  const center: [number, number] = useMemo(() => {
    if (initialCenter) return initialCenter;
    return [51.1657, 10.4515];
  }, [initialCenter]);

  const startDrawing = () => {
    setDraftPoints([]);
    setMode('drawing');
  };
  const finishDrawing = () => {
    if (draftPoints.length < 3) return;
    if (draftClosingCrosses(draftPoints)) {
      window.alert('Das Polygon würde sich beim Schließen selbst überschneiden. Bitte einen Punkt verschieben oder hinzufügen.');
      return;
    }
    setRings([...rings, draftPoints]);
    setDraftPoints([]);
    setMode('view');
  };
  const cancelDrawing = () => {
    setDraftPoints([]);
    setMode('view');
  };
  const undoLastPoint = () => {
    if (draftPoints.length === 0) return;
    setDraftPoints(draftPoints.slice(0, -1));
  };
  const startEditing = () => setMode('editing');
  const stopEditing = () => setMode('view');
  const deleteAll = () => {
    if (!window.confirm('Polygon wirklich löschen?')) return;
    setRings([]);
    setMode('view');
  };

  let helpText: string;
  if (mode === 'drawing') {
    helpText = `Klicke Ecke für Ecke auf die Karte. ${draftPoints.length} ${draftPoints.length === 1 ? 'Punkt' : 'Punkte'} gesetzt — mindestens 3 nötig.`;
  } else if (mode === 'editing') {
    helpText = 'Ecken ziehen zum Verschieben. Rechtsklick auf eine Ecke entfernt sie.';
  } else if (rings.length === 0) {
    helpText = 'Klicke „Polygon zeichnen", um die Fläche des B-Plans auf der Karte zu erfassen.';
  } else {
    helpText = 'Polygon kann bearbeitet, gelöscht oder um weitere Teilflächen erweitert werden.';
  }

  return (
    <div className="space-y-2">
      {!readOnly && (
        // Fixe Höhe verhindert, dass die Karte beim Modus-Wechsel hoch/runter springt.
        <div className="min-h-[5rem] flex flex-col gap-2">
          <div className="text-sm text-gray-600">{helpText}</div>
          <div className="flex flex-wrap gap-2 items-center">
            {mode === 'view' && (
              <>
                <button
                  type="button"
                  onClick={startDrawing}
                  className="btn btn-primary px-3 py-1.5"
                >
                  {rings.length === 0 ? 'Polygon zeichnen' : 'Weiteres Polygon hinzufügen'}
                </button>
                {rings.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={startEditing}
                      className="btn btn-outline px-3 py-1.5 bg-white"
                    >
                      Ecken bearbeiten
                    </button>
                    <button
                      type="button"
                      onClick={deleteAll}
                      className="btn btn-outline border-danger-200 text-danger-700 enabled:hover:bg-danger-50 focus-visible:ring-danger-500 px-3 py-1.5 bg-white"
                    >
                      Polygon löschen
                    </button>
                  </>
                )}
              </>
            )}
            {mode === 'drawing' && (
              <>
                <button
                  type="button"
                  onClick={finishDrawing}
                  disabled={draftPoints.length < 3}
                  className="btn btn-primary px-3 py-1.5"
                >
                  Fertig ({draftPoints.length})
                </button>
                <button
                  type="button"
                  onClick={undoLastPoint}
                  disabled={draftPoints.length === 0}
                  className="btn btn-outline px-3 py-1.5 bg-white"
                >
                  Letzten Punkt entfernen
                </button>
                <button
                  type="button"
                  onClick={cancelDrawing}
                  className="btn btn-outline px-3 py-1.5 bg-white"
                >
                  Abbrechen
                </button>
              </>
            )}
            {mode === 'editing' && (
              <button
                type="button"
                onClick={stopEditing}
                className="btn btn-primary px-3 py-1.5"
              >
                Fertig
              </button>
            )}
          </div>
        </div>
      )}

      <div style={{ height, width: '100%' }} className="rounded-lg overflow-hidden border border-gray-200">
        <MapContainer
          center={center}
          zoom={initialCenter ? 13 : 6}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <DrawLayer
            rings={rings}
            setRings={setRings}
            mode={mode}
            draftPoints={draftPoints}
            setDraftPoints={setDraftPoints}
            fitOnLoad={fitOnLoad}
            setFitOnLoad={setFitOnLoad}
          />
        </MapContainer>
      </div>
    </div>
  );
}
